import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table } from "@tanstack/react-table";
import { debounce } from "lodash";
import { X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Label } from "components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import { useFilterActions } from "../../ducks/hooks/action";
import { useFilterState } from "../../ducks/hooks/state";
import { UF_OPTIONS_ENGINE_FILTER, TAX_OPTIONS } from "../../utils/utils";
import { ModernCard } from "../modern-card";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
}

export function DataTableToolbar<TData>({}: DataTableToolbarProps<TData>) {
  const { updateFilter, clearFilters } = useFilterActions();

  const filterState = useFilterState();
  const [searchValue, setSearchValue] = useState(
    filterState.filters.search || ""
  );

  const [searchValueNcm, setSearchValueNcm] = useState(
    filterState.filters.search_ncm || ""
  );

  const debouncedSearchUpdate = useCallback(
    debounce((value: string) => {
      updateFilter("search", value);
    }, 500),
    []
  );
  const debouncedSearchUpdateNcm = useCallback(
    debounce((value: string) => {
      updateFilter("search_ncm", value);
    }, 500),
    []
  );

  const [searchValueSku, setSearchValueSku] = useState(
    filterState.filters.search_sku || ""
  );

  useEffect(() => {
    if (!filterState.filters.search_ncm) {
      setSearchValueNcm("");
    }
    if (!filterState.filters.search_sku) {
      setSearchValueSku("");
    }
  }, [filterState.filters.search_ncm, filterState.filters.search_sku]);
  const debouncedSearchUpdateSku = useCallback(
    debounce((value: string) => {
      updateFilter("search_sku", value);
    }, 500),
    []
  );
  const normalizeSearch = (value: string) =>
    value.trim().toLowerCase() === "todos" ? "all" : value;

  const handleSearchChangeNcm = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const normalizedValue = normalizeSearch(event.target.value);
    setSearchValueNcm(event.target.value);
    debouncedSearchUpdateNcm(normalizedValue);
  };

  const handleSearchChangeSku = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const normalizedValue = normalizeSearch(event.target.value);
    setSearchValueSku(event.target.value);
    debouncedSearchUpdateSku(normalizedValue);
  };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedSearchUpdate(value);
  };
  const currentType =
    filterState.filters.type === "PRODUCT" || filterState.filters.type === "NCM"
      ? filterState.filters.type
      : "NCM";

  return (
    <>
      <ModernCard title="Filtros de Pesquisa" description="" className="w-full">
        <div className="flex flex-wrap items-end gap-6 mb-5">
          <div className="flex flex-col w-56">
            <Label
              htmlFor="rule-name-input"
              className="text-sm font-semibold text-gray-700 mb-1"
            >
              Buscar por Nome da Regra
            </Label>
            <Input
              id="rule-name-input"
              placeholder="Digite o nome da regra"
              value={searchValue}
              onChange={handleSearchChange}
              className="px-4 py-2 border rounded-xl text-gray-700 shadow-sm"
            />
          </div>

          {currentType === "NCM" && (
            <div className="flex flex-col w-56">
              <Label
                htmlFor="ncm-input"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Buscar por NCM
              </Label>
              <Input
                id="ncm-input"
                placeholder="Digite o NCM"
                value={searchValueNcm}
                onChange={handleSearchChangeNcm}
                className="px-4 py-2 border rounded-xl text-gray-700 shadow-sm"
              />
            </div>
          )}

          {currentType === "PRODUCT" && (
            <div className="flex flex-col w-56">
              <Label
                htmlFor="sku-input"
                className="text-sm font-semibold text-gray-700 mb-1"
              >
                Buscar por SKU
              </Label>
              <Input
                id="sku-input"
                placeholder="Digite o SKU"
                value={searchValueSku}
                onChange={handleSearchChangeSku}
                className="px-4 py-2 border rounded-xl text-gray-700 shadow-sm"
              />
            </div>
          )}

          <div className="flex flex-col w-56">
            <Label
              htmlFor="exception-type-filter"
              className="text-sm font-semibold text-gray-700 mb-1"
            >
              Filtrar por Tributo
            </Label>
            <Select
              value={filterState.filters.exception_type || "all"}
              onValueChange={(value) => {
                updateFilter(
                  "exception_type",
                  value === "all" ? undefined : value
                );
              }}
            >
              <SelectTrigger className="px-4 py-2 border rounded-xl text-gray-700 shadow-sm">
                <SelectValue placeholder="Selecione um Tributo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {TAX_OPTIONS.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col w-56">
            <Label
              htmlFor="state-filter"
              className="text-sm font-semibold text-gray-700 mb-1"
            >
              Filtrar por Estado
            </Label>
            <Select
              value={filterState.filters.state || "all"}
              onValueChange={(value) => {
                updateFilter("state", value === "all" ? undefined : value);
              }}
            >
              <SelectTrigger className="px-4 py-2 border rounded-xl text-gray-700 shadow-sm">
                <SelectValue placeholder="Selecione um Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {UF_OPTIONS_ENGINE_FILTER.map((state) => (
                  <SelectItem key={state} value={state}>
                    {state}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="border-t-2 border-gray-200">
          {Object.keys(filterState.filters).some(
            (k) => filterState.filters[k as keyof typeof filterState.filters]
          ) && (
            <Button
              variant="default"
              onClick={() => {
                clearFilters({ type: filterState.filters.type });
                setSearchValue("");
                setSearchValueNcm("");
                setSearchValueSku("");
              }}
              className="h-10 mt-5 px-4 py-2 border rounded-xl shadow-sm border-transparent bg-transparent hover:bg-[#F5CB2B]"
            >
              <X className="w-5 h-5 text-gray-700" />
              <span className="text-gray-700 font-semibold text-sm">
                Resetar Filtros
              </span>
            </Button>
          )}
        </div>
      </ModernCard>
    </>
  );
}
